import { cx, css } from '@emotion/css';
import { Space, Button } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as SvgForward } from 'assets/icons/forward.svg';
import { openExternalURL } from 'utils/openExternalURL';
import { useTranslate } from '@tolgee/react';
import operateBtns from 'components/Refund/OperateBtns';
import { Detail } from 'components/Refund';
import ContactBuyerBtn from '../ContactBuyerBtn';
import CancelApprovalBtns from '../CancelApprovalBtns';
import { SimpleProcessStatus as SimpleStatus } from '../../utils/status';
import { toUpperCase } from 'utils/toLowerCase';
import type { RefundDrawerDataSource } from './types';
import type { ReturnOrderListItemV2 } from 'services/api/refund';

export interface RefundDrawerFooterProps {
  trade: Conversation.Trade.TradeWithLogistics | undefined;
  operatable: boolean;
  platform: General.Platform;
  isRefund: boolean;
  openReturn: boolean;
  returnSn: string;
  dataSource: RefundDrawerDataSource;
  simpleStatus: SimpleStatus;
  conversation?: Conversation.Conversation;
  pullTrade: (refresh?: boolean) => void;
  onUpdate?: () => void;
  refreshList?: () => void;
  row: ReturnOrderListItemV2 | import('services/api/refund').CancelOrder;
  type: 'refund' | 'cancel';
}

/**
 * 退款抽屉底部操作区：联系买家、详情按钮、操作按钮（退款操作 / 取消审批）。
 * 纯展示与交互，显隐与数据由容器通过 props 传入。
 */
export function RefundDrawerFooter({
  trade,
  operatable,
  platform,
  isRefund,
  openReturn,
  returnSn,
  dataSource,
  simpleStatus,
  conversation,
  pullTrade,
  onUpdate,
  refreshList,
  row,
  type,
}: RefundDrawerFooterProps) {
  const { t } = useTranslate();

  if (!trade || !operatable) return null;

  let detailBtn: React.ReactNode = null;
  if (platform === 'shopee' && isRefund) {
    if (openReturn) {
      detailBtn = (
        <Detail
          externalStoreId={trade.externalShopId}
          externalTradeId={trade.externalTradeId}
          returnSn={returnSn}
          size="middle"
        />
      );
    } else if (trade.storeType === 'CROSS') {
      detailBtn = (
        <Button
          type="default"
          size="small"
          onClick={() => {
            openExternalURL(
              `https://seller.shopee.cn/portal/sale/returnlist?cnsc_shop_id=${trade.externalShopId}`,
              '__blank',
            );
          }}
        >
          {t('trade.卖家中心退款详情')} <Icon component={SvgForward} />
        </Button>
      );
    }
  }

  let ctrlBtns: React.ReactNode = null;
  if (platform === 'shopee') {
    if (isRefund) {
      const refundRow = row as ReturnOrderListItemV2;
      const {
        operate,
        internalStoreId,
        unit,
        refundAmount,
        latestOfferAmount,
        offerCounterLimit,
        operationLog = [],
        externalStoreId,
        allowOffer,
        displayStatus,
        internalTradeId,
        preDispute,
        region,
        externalBuyerId,
        buyerNick,
        orderSn,
      } = refundRow;
      ctrlBtns = operateBtns({
        externalStoreId,
        externalTradeId: trade.externalTradeId,
        returnSn,
        operate,
        refreshList,
        internalStoreId,
        internalTradeId: `${internalTradeId}`,
        displayStatus,
        unit,
        refundAmount,
        latestOfferAmount,
        allowOffer,
        offerCounterLimit,
        operationLog,
        platform: platform.toUpperCase() as General.PlatformUpperCase,
        uiContactBuyerBtn: (
          <ContactBuyerBtn
            orderSn={orderSn}
            region={toUpperCase(region)}
            externalBuyerId={externalBuyerId}
            externalStoreId={externalStoreId}
            buyerNick={buyerNick}
            returnSn={returnSn}
            type="refund"
            onClose={refreshList}
            conversation={conversation}
          />
        ),
        size: 'middle',
        preDispute,
        region: toUpperCase(row.region) as General.UpperCaseRegion,
      });
    } else {
      ctrlBtns =
        simpleStatus === SimpleStatus.Pending && conversation ? (
          <div
            className={cx(css`
              button + button {
                margin-left: 10px;
              }
            `)}
          >
            <CancelApprovalBtns
              internalStoreId={conversation.storeId}
              externalStoreId={trade.externalShopId}
              orderId={dataSource.orderSn}
              platform={platform}
              region={toUpperCase(row.region) as General.UpperCaseRegion}
              onUpdate={() => {
                pullTrade(true);
                onUpdate?.();
              }}
              buttonProps={{ type: 'default', size: 'middle' }}
            />
          </div>
        ) : null;
    }
  }

  const showContactBuyer = !(
    platform === 'mercado' && (row as ReturnOrderListItemV2).returnStatus === 'closed'
  );

  return (
    <div
      className={cx(css`
        position: absolute;
        bottom: 0;
        left: 0;
        height: 56px;
        width: 100%;
        padding: 0 14px;
        background-color: #fff;
        display: flex;
        border-top: 1px solid #ccc;
        align-items: center;
        justify-content: space-between;
        box-sizing: border-box;
        overflow-x: scroll;
      `)}
    >
      <Space size={16}>
        {showContactBuyer ? (
          <ContactBuyerBtn
            orderSn={dataSource.orderSn}
            region={toUpperCase(dataSource.region) as General.UpperCaseRegion}
            externalBuyerId={dataSource.externalBuyerId}
            externalStoreId={dataSource.externalStoreId || ''}
            buyerNick={dataSource.buyerNick || ''}
            returnSn={dataSource.returnSn}
            reverseOrderId={dataSource.reverseOrderId}
            type={type}
            conversation={conversation}
          />
        ) : null}
        {ctrlBtns}
      </Space>
      {detailBtn}
    </div>
  );
}
